import { routes } from "@/global/routes";
import {
  faqs,
  howItWorks,
  TampaBayRaysTCListItems,
  TampaBayRaysCheckoutTerms,
  TampaBayRaysAdditionalDetailsDisclaimerTC,
  AnalyticsHeader,
} from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import { DefaultRegistrationTerms } from "../../default_terms_and_conditions";

import Logo from "public/images/tampabayrays/logo.png";
import Background from "public/images/tampabayrays/background.jpg";
import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";

const Banner =
  "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/TB%20Rays%20Suite%20Logo%20Assets/1486x148_Banner.jpg";

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "tampabayrays_suites",
  browserTitle: "Tampa Bay Rays Suites",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  mlbTeamId: 139,
  teamName: "Tampa Bay Rays",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10172-bayrays-sec",
    mapId: "blockmap2d",

    suiteImages: {
      S16: [
        "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Suite%2016%20-%201.jpeg",
        "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Suite%2016%20-%202.jpeg",
      ],
      S24: [
        "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Baseline%20Suite%2024%20-%201.jpg",
        "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Baseline%20Suite%2024%20-%202.jpg",
        "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Baseline%20Suite%2024%20-%203.jpg",
      ],
      S30: [
        "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Outfield%20Suite%2030%20-%201.jpg",
        "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Outfield%20Suite%2030%20-%202.jpg",
      ],
      S9: [
        "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Suite%209%20-%201.jpeg",
        "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Suite%209%20-%202.jpeg",
      ],
      S35: [
        "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Suite%2035%20-%201.jpeg",
      ],
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.suiteLevelV2,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    maxGameSelectionCount: 2,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    hideViewTicketsInThankYouPage: true,

    isCheckoutAdditionalDetailsEnabled: true,
    isPerksEnabled: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    googleTagManagerId: "",
    AnalyticsHeader: AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl: "https://www.mlb.com/rays/tickets/my-tickets",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",
  shouldUseInternalLogin: false,

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: Logo.src,
    findGamesBannerImage: Banner,
    authBackgroundImage: Background.src,
    checkoutStatusPageImage: Background.src,
    giftCardStandalonePageImage: Background.src,
    logoImage: Logo.src + "?width=128&height=66",
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    hidePackageBalanceOnCheckout: false,
    hideServiceFee: false,
    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    inventoryCardSettings: {
      isPBLineItemsVisible: true,
      seatCountLabel: "Tickets Included",
      inventorySuitePackageInfo: "$600",
      pbLineItemsValue: 5,
      hideFBPackageLineItem: true,
    },
    inventoryContainer: {
      emptyStateTitle: "Please select another game.",
      emptyStateDesc: " ",
      getGridCellHeight: (arg) => (arg?.mdDown ? 240 : 150),
    },
    checkoutPerkSubtotals: {
      ticketTotalLabel: "Total Ticket Balance",
      serviceFeeLabel: "Order Service Fee",
    },

    perks: {
      hideAddOnsViewer: true,
      hideCateringPerkItem: true,
    },

    eventCardSettings: {
      soldOutTagLabel:
        "Limited Inventory. Contact groupsales@raysbaseball.com for options.",
    },
  },

  colors: {
    primary: "#092C5C",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: <TampaBayRaysTCListItems />,
    checkoutTermsAndCondition: <TampaBayRaysCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    additionalDetailsDisclaimerTC: (
      <TampaBayRaysAdditionalDetailsDisclaimerTC />
    ),
    purchaseSeats: {
      chooseAddOnsTabTitle: "Confirm Tickets",
      progressBarSuccessText:
        "You're good to go! Add more games or select Continue to view seating options.",
      progressBarAditionalText: " to continue!",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Tampa Bay Rays Suites confirmation was successful! A representative will be in touch shortly to follow up with any questions you may have.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
